import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { GameOption } from "entities/game/types";
import { useMetadataTagsListQuery } from "entities/metadataTags/queries/useMetadataTagsListQuery";
import { gamesOrderTypeOptions } from "entities/modules/constants";
import { useTagBlocksStore } from "entities/modules/stores/useTagBlocksStore";
import { GamesOrderType, GamesModuleLayout } from "entities/modules/types";
import { useThemePrimaryTagsQuery } from "entities/themeTagTypes/queries/useThemePrimaryTagsQuery";
import { FC, useCallback, useMemo } from "react";
import AddManualGamesField from "shared/components/AddManualGamesField";
import ContentSection from "shared/components/Drawer/components/ContentSection";
import Select from "shared/components/Form/Select";
import Switch from "shared/components/Form/Switch";
import ManualSelectedGamesList from "shared/components/ManualSelectedGamesList";
import RandomSelectedGamesList from "shared/components/RandomSelectedGamesList";
import { COLOR_BLACK } from "shared/theme/colors";
import { formatTagsWithTypes } from "widgets/ModuleTagsWidget/utils";
import { useShallow } from "zustand/react/shallow";
import { useModuleDetails } from "../../../../context";
import { useGeneralData } from "../../../../stores/useGeneralData";
import { useSelectedGamesData } from "../../../../stores/useSelectedGamesData";
import StudioOrdering from "../StudioOrdering";

// TODO: Make this Component as Widget to reduce codebase duplications
const SelectedGames: FC = () => {
  const { isLoadingSelectedGamesList, availableStudios } = useModuleDetails();

  const { studioFilterEnabled, setStudioFilterEnabled, moduleLayout } =
    useGeneralData((state) => ({
      studioFilterEnabled: state.studioFilterEnabled,
      setStudioFilterEnabled: state.setStudioFilterEnabled,
      moduleLayout: state.moduleLayout,
    }));

  const { tagBlocks } = useTagBlocksStore();
  const { data: metadataTags } = useMetadataTagsListQuery();
  const { data: themePrimaryTags } = useThemePrimaryTagsQuery();

  const {
    orderType,
    selectedGames,
    selectedGamesByTag,
    selectedGamesManually,
    setOrderType,
    setSelectedGames,
    setSelectedGamesManually,
  } = useSelectedGamesData(
    useShallow((state) => ({
      orderType: state.orderType,
      selectedGames: state.selectedGames,
      selectedGamesByTag: state.selectedGamesByTag,
      selectedGamesManually: state.selectedGamesManually,
      setOrderType: state.setOrderType,
      setSelectedGames: state.setSelectedGames,
      setSelectedGamesManually: state.setSelectedGamesManually,
    })),
  );

  const hasRandomOrder = useMemo(() => {
    return studioFilterEnabled || orderType === GamesOrderType.Random;
  }, [studioFilterEnabled, orderType]);

  const shouldHideStudioFilter =
    moduleLayout === GamesModuleLayout.BigHorizontalTiles ||
    moduleLayout === GamesModuleLayout.Top10Games;

  const tagSelectedGameIds = useMemo(() => {
    return selectedGamesByTag.map((game) => game.id);
  }, [selectedGamesByTag]);

  const handleChange = (value: string) => {
    setOrderType(value as GamesOrderType);
  };

  const formatSelectedGamesHints = useCallback(
    (game: GameOption) => {
      return formatTagsWithTypes({
        tagBlocks,
        option: game,
        themePrimaryTags,
        metadataTags,
      });
    },
    [tagBlocks, themePrimaryTags, metadataTags],
  );

  return (
    <Stack spacing="1rem">
      <ContentSection title="GAMES">
        {!shouldHideStudioFilter && (
          <Box marginBottom="1rem">
            <Switch
              value={studioFilterEnabled}
              onChange={setStudioFilterEnabled}
              label="Insert studio filter buttons"
            />
          </Box>
        )}
        {!studioFilterEnabled && (
          <Select
            label="Games Order"
            labelColor={COLOR_BLACK}
            placeholder="Select an order"
            disabledPlaceholder
            fullWidth
            required
            value={orderType}
            handleChange={handleChange}
            options={gamesOrderTypeOptions}
          />
        )}
        <Box marginTop="1rem">
          <AddManualGamesField
            selectedGamesManually={selectedGamesManually}
            setSelectedGamesManually={setSelectedGamesManually}
            formatSelectedGamesHints={formatSelectedGamesHints}
            tagSelectedGameIds={tagSelectedGameIds}
            availableStudios={availableStudios || []}
          />
        </Box>
        {!studioFilterEnabled && hasRandomOrder && (
          <RandomSelectedGamesList
            selectedGames={selectedGames}
            setSelectedGames={setSelectedGames}
          />
        )}
        {!studioFilterEnabled && !hasRandomOrder && (
          <ManualSelectedGamesList
            isLoadingSelectedGamesList={isLoadingSelectedGamesList}
            selectedGames={selectedGames}
            setSelectedGames={setSelectedGames}
          />
        )}
        {studioFilterEnabled && <StudioOrdering />}
      </ContentSection>
    </Stack>
  );
};

export default SelectedGames;
